import { canCreateUsers } from '@/auth/roles';
import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createCoachProfile, createUser, getCoaches } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { AllTimeZones, findTimeZoneByValue } from '@/lib/time-zones';
import { CreateCoachForm } from '@/routes/_layout/teachers/-components/create-coach-form';
import { createCoachSchema, createUserOptionsFromFormData } from '@/routes/_layout/teachers/-components/form-schema';
import { coach } from '@airreading/common';
import { UpdateCoachOptions, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

export interface CreateCoachProps {
  trigger?: ReactNode;
  fromUser?: UserWithRolesAndProfiles;
}

const formSchema = createCoachSchema;
type Form = z.infer<typeof formSchema>;

export const CreateCoach = ({ trigger, fromUser }: CreateCoachProps) => {
  const { mutateAsync: createCoachProfileMutate } = useMutation({
    mutationFn: createCoachProfile,
  });

  const { mutateAsync: createUserMutate } = useMutation({
    mutationFn: createUser,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();

  const onSubmit = ({ formData, closeModal }: SubmitProps<Form>) => {
    try {
      const createUserPayload = createUserOptionsFromFormData(formData);
      const { smsReminderOptedIn, displayName, secondaryEmail } = formData;

      const coachProfilePayload: UpdateCoachOptions = {
        smsReminderOptedIn: smsReminderOptedIn,
        displayName,
        secondaryEmail: secondaryEmail || undefined,
      };

      const apiCall = fromUser
        ? () => createCoachProfileMutate({ userId: fromUser.id, data: coachProfilePayload })
        : () =>
            createUserMutate({
              ...createUserPayload,
              assignRoles: [coach()],
              coachProfile: coachProfilePayload,
            });

      return apiCall()
        .then(() => {
          toast({
            title: 'Teacher created successfully',
          });
          closeModal();
          queryClient.invalidateQueries(getCoaches());
        })
        .catch((error) => {
          const airServerError = airServerErrorFrom(error);
          const errorMessage = airServerError?.userFacingMessage || 'Teacher creation failed';

          toast({
            title: 'Teacher creation failed',
            description: errorMessage,
          });
        });
    } catch (error: unknown) {
      if (error instanceof TypeError) {
        toast({ title: error.message });
        return;
      }
    }
  };

  // With `fromUser` this only adds a coach profile to an existing user, which
  // is not user creation.
  if (!fromUser && !canCreateUsers(actingUser)) {
    return null;
  }

  return (
    <FormModal
      modalTitle="Add teacher"
      dialogContentProps={{ className: 'max-w-3xl' }}
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger || <Button>Add teacher</Button>}
      confirmBtnLabel="Add"
      defaultValues={{
        ...(fromUser
          ? {
              firstName: fromUser.firstName ?? undefined,
              lastName: fromUser.lastName ?? undefined,
              email: fromUser.email ?? undefined,
              phone: fromUser.phone ?? undefined,
              timeZone: findTimeZoneByValue(fromUser.timeZone) ?? AllTimeZones[0],
              displayName: fromUser.coachProfile?.displayName ?? undefined,
              secondaryEmail: fromUser.coachProfile?.secondaryEmail ?? undefined,
              smsReminderOptedIn: fromUser.coachProfile?.smsReminderOptedIn ?? true,
            }
          : {}),
      }}
    >
      {({ form }) => {
        return (
          <div className="grid grid-cols-2 gap-2 py-4">
            <CreateCoachForm form={form} disabled={!!fromUser} />
          </div>
        );
      }}
    </FormModal>
  );
};
